import { createApiRef } from '@backstage/frontend-plugin-api';
import type { UploadAssessment } from '@internal/plugin-cloudflare-workers-common';

export type Route = {
  pattern: string;
  /**
   * True when this route's hostname is an intentionally public,
   * unauthenticated site (e.g. on `make.com`). Optional for backward
   * compatibility with cached/older backend responses.
   */
  public?: boolean;
};

export type DeploymentVersion = {
  versionId: string;
  percentage: number;
  tag: string | null;
};

export type ActiveDeployment = {
  id: string;
  source: string;
  strategy: string;
  versions: DeploymentVersion[];
  createdOn: string;
  message: string | null;
  annotations: Record<string, string>;
};

export type AccessDecision = 'allow' | 'deny' | 'non_identity' | 'bypass';

export type AccessPolicySummary = {
  id: string;
  name: string;
  decision: AccessDecision;
  allowedEmails: string[];
  allowedDomains: string[];
};

export type AccessAppData = {
  id: string;
  name: string;
  domain: string;
  type: string;
  sessionDuration: string;
  policies: AccessPolicySummary[];
  createdAt: string;
  updatedAt: string;
};

export type EmailRestrictionStatus = {
  expectedEmails: string[];
  actualEmails: string[];
  missingEmails: string[];
  extraEmails: string[];
  matched: boolean;
};

/**
 * Per-address access verdict. Cloudflare Zero Trust Access applications are
 * bound to hostnames (DNS-level), so each address a worker answers on is gated
 * independently — two addresses of the same worker can differ.
 */
export type AddressAccess = {
  hostname: string;
  protected: boolean;
};

export type AccessStatusSummary = {
  /** True only when *every* reachable address requires sign-in. */
  protected: boolean;
  hasEmailRestriction: boolean;
  /** Per-address protection, one entry per reachable hostname. */
  addresses: AddressAccess[];
};

export type AccessStatusDetail = {
  protected: boolean;
  app: AccessAppData | null;
  idpNames: string[];
  emailRestriction: EmailRestrictionStatus | null;
};

export type WorkerSummary = {
  name: string;
  routes: Route[];
  workersDevUrl: string;
  /** Cloudflare `modified_on`: moves on any change, including a secret edit. */
  lastDeployedAt: string;
  /** Last code publish (SHA-bearing deployment); null when unknown. */
  lastPublishedAt: string | null;
  /** `unavailable` when the deployment history could not be fetched. */
  publishHistory: PublishHistoryStatus;
  lastDeployedFrom: string;
  compatibilityDate: string;
  tags: string[];
  activeDeployment: ActiveDeployment | null;
  access: SectionResult<AccessStatusSummary>;
};

export type WorkersListResponse = {
  workers: WorkerSummary[];
  cached: boolean;
  cachedAt: string | null;
  stale: boolean;
  backgroundError: string | null;
};

export type PublishHistoryStatus = 'ok' | 'unavailable';

export type IdleReason = 'no-visits' | 'no-publish';

export type IdleThresholds = {
  visitsDays: number;
  publishDays: number;
  rule: 'any' | 'visits';
};

/**
 * `named`: an email ref, or a user/group that exists in the catalog.
 * `not-in-org`: a valid GitHub username with no catalog User (outside the
 * imported GitHub org, or not synced yet); still someone to ask.
 * `nobody`: anything else (not a GitHub username, a missing group, any kind).
 */
export type OwnerStatus = 'named' | 'not-in-org' | 'nobody';

export type AppCatalogFacts = {
  entityRef: string;
  /** Catalog-normalized owner ref; null when the entity has none. */
  ownerRef: string | null;
  ownerStatus: OwnerStatus;
  /** Edit link for the app's `catalog-info.yaml`. */
  editUrl: string | null;
  /** Source directory of the app. */
  sourceUrl: string | null;
};

/** In next-step order: the first finding decides the next step. */
export type AppFinding =
  | 'idle-public'
  | 'idle-unlinked'
  | 'owner-nobody'
  | 'idle-owned';

export type NextStepKind =
  | 'secure-or-retire'
  | 'remove'
  | 'fix-owner'
  | 'ask-owner';

export type NextStep = {
  kind: NextStepKind;
  addressee: 'owner' | 'devprod';
};

/** Usage and maintenance facts for one app on the App activity page. */
export type AppActivityEntry = {
  name: string;
  /** Visits in the last 30 / 60 / 90 days (cumulative from now). */
  visits: { d30: number; d60: number; d90: number };
  /** Last day (`YYYY-MM-DD`) with a recorded visit; null when none in 90 days. */
  lastVisitOn: string | null;
  /** Last code publish; null when unknown. */
  lastPublishedAt: string | null;
  /** `unavailable`: history not fetched; `no-publish` is then not evaluated. */
  publishHistory: PublishHistoryStatus;
  /** Any change (`modified_on`). */
  lastChangedAt: string;
  /** Both reasons are always evaluated; `value` applies the configured rule. */
  idle: { value: boolean; reasons: IdleReason[] };
  /** Linked Component; null when not linked or the catalog is unavailable. */
  catalog: AppCatalogFacts | null;
  /** At least one address has no sign-in; null when access was not read. */
  reachableWithoutSignIn: boolean | null;
  findings: AppFinding[];
  nextStep: NextStep | null;
  cloudflareDashboardUrl: string;
};

export type AppActivityResponse = {
  entries: AppActivityEntry[];
  window: { from: string; to: string; days: number };
  thresholds: IdleThresholds;
  /** Set when the catalog could not be read; owner and linkage are unknown. */
  catalogError: string | null;
  /** On error the visits columns are unavailable; the rest still renders. */
  analytics: SectionResult<{ sampled: boolean }>;
  cached: boolean;
  cachedAt: string | null;
  stale: boolean;
  /** Last background analytics refresh failure while serving stale data. */
  backgroundError: string | null;
};

export type KvBinding = {
  name: string;
  namespaceId: string;
};

export type EnvVariable = {
  name: string;
  value: string;
};

export type SettingsData = {
  kvNamespaces: KvBinding[];
  envVars: EnvVariable[];
  secretNames: string[];
  compatibilityFlags: string[];
};

export type SchedulesData = {
  cronTriggers: string[];
};

export type WorkerMetricsRange = '1h' | '24h' | '7d' | '30d';

export type WorkerMetricsPoint = {
  timestamp: string;
  requests: number;
  errors: number;
  errorRate: number;
};

/** CPU/wall-time percentiles for one time bucket, in microseconds. */
export type WorkerLatencyPoint = {
  timestamp: string;
  cpuP50: number;
  cpuP99: number;
  wallP50: number;
  wallP99: number;
};

/** Range-wide CPU/wall-time percentiles, in microseconds. */
export type WorkerLatencyStats = {
  cpuP50: number;
  cpuP99: number;
  wallP50: number;
  wallP99: number;
};

export type WorkerStatusBreakdownEntry = {
  status: string;
  requests: number;
};

export type WorkerColoBreakdownEntry = {
  coloCode: string;
  requests: number;
  errors: number;
};

export type WorkerMetricsResponse = {
  range: WorkerMetricsRange;
  totals: {
    requests: number;
    errors: number;
    errorRate: number;
  };
  series: WorkerMetricsPoint[];
  statusBreakdown: WorkerStatusBreakdownEntry[];
  latencySeries: WorkerLatencyPoint[];
  latencyTotals: WorkerLatencyStats | null;
  coloBreakdown: WorkerColoBreakdownEntry[];
  cached: boolean;
  cachedAt: string | null;
  stale: boolean;
  backgroundError: string | null;
};

export type WorkerLogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

export type WorkerLogEntry = {
  id: string;
  /** Unix epoch in milliseconds. */
  timestamp: number;
  level: WorkerLogLevel;
  message: string;
  error: boolean;
  eventType: string | null;
  outcome: string | null;
  truncated: boolean;
};

export type WorkerLogsResponse = {
  range: WorkerMetricsRange;
  errorsOnly: boolean;
  /** Newest first. */
  entries: WorkerLogEntry[];
  nextCursor: string | null;
  totalMatched: number | null;
  cached: boolean;
  cachedAt: string | null;
};

export type SectionResult<T> = {
  data: T | null;
  error: string | null;
};

export type DeploymentHistoryEntry = {
  id: string;
  source: string;
  strategy: string;
  versions: DeploymentVersion[];
  createdOn: string;
  message: string | null;
  /** Short git commit SHA parsed from the deploy message, or null. */
  commitSha: string | null;
};

export type VersionConfigBinding = {
  type: string;
  name: string;
};

/**
 * Normalized configuration of one immutable Worker version, used to diff config
 * between versions. Secret values are never exposed by Cloudflare (names only).
 */
export type VersionConfig = {
  versionId: string;
  number: number | null;
  createdOn: string | null;
  source: string | null;
  authorEmail: string | null;
  envVars: EnvVariable[];
  kvNamespaces: KvBinding[];
  secretNames: string[];
  otherBindings: VersionConfigBinding[];
  compatibilityDate: string;
  compatibilityFlags: string[];
  cached: boolean;
  cachedAt: string | null;
};

export type WorkerDetailResponse = {
  name: string;
  routes: Route[];
  workersDevUrl: string;
  lastDeployedAt: string;
  lastDeployedFrom: string;
  compatibilityDate: string;
  tags: string[];
  activeDeployment: ActiveDeployment | null;
  deploymentHistory: DeploymentHistoryEntry[];
  dashboardUrl: string;
  settings: SectionResult<SettingsData>;
  schedules: SectionResult<SchedulesData>;
  access: SectionResult<AccessStatusDetail>;
  cached: boolean;
  cachedAt: string | null;
};

/**
 * The app's declarative configuration as committed to the vibe monorepo
 * (`apps/<name>/wrangler.jsonc` on the default branch) — the source of truth
 * CI deploys from. Effective values have wrangler's defaulting rules applied
 * (e.g. previews default OFF once a custom domain exists).
 */
export type WorkerRepoConfig = {
  found: boolean;
  appDir: string;
  wranglerUrl?: string;
  catalogInfoUrl?: string;
  configName?: string | null;
  previewUrlsEffective?: boolean;
  previewUrlsExplicit?: boolean | null;
  observabilityEffective?: boolean;
  observabilityExplicit?: boolean | null;
  customDomains?: string[];
  otherRoutes?: string[];
  cached: boolean;
  cachedAt: string | null;
};

export type RefreshResponse = {
  refreshed?: boolean;
  message?: string;
  error?: string;
  retryAfter?: number;
};

export type UploadProjectResponse = {
  uploadId: string;
  sizeBytes: number;
};

export type VerificationState = 'pass' | 'pending' | 'fail';

export type PendingUpdate = {
  app: string;
  kind: 'update' | 'import';
  pullRequestUrl: string;
  pullRequestNumber: number;
  title: string;
  createdAt: string;
  updatedAt: string;
  headSha: string;
  /** Unique title part for portal display (PR title without `app:`). */
  displayTitle: string;
  /** First human sentence of the PR body. */
  summary?: string;
  previewUrl: string;
  previewState: 'ready' | 'pending' | 'stale';
  canManage: boolean;
  verification: {
    rollup: VerificationState;
    checks: Array<{
      name: string;
      state: VerificationState;
      required: true;
      missing?: boolean;
    }>;
    findings: VerificationFinding[];
  };
};

export type VerificationFinding = {
  gate: string;
  severity: 'critical' | 'major' | 'minor' | 'nit';
  message: string;
  path?: string;
};

/**
 * Where in the publish pipeline an app's most recent change is. Mirrors the
 * backend's `PublishStatusState` (see PublishStatusService):
 * `checks_running` / `checks_failed` cover the open draft PR, `publishing` /
 * `deploy_failed` the post-merge deploy, `live` means reachable at its real
 * address, `abandoned` a closed-unmerged draft, and `none` means no recent
 * draft explains this app.
 */
export type PublishStatusState =
  | 'checks_running'
  | 'checks_failed'
  | 'publishing'
  | 'deploy_failed'
  | 'abandoned'
  | 'live'
  | 'none';

export type PublishStatus = {
  app: string;
  state: PublishStatusState;
  kind?: 'update' | 'import';
  pullRequestUrl?: string;
  pullRequestNumber?: number;
  /** Raw names of failed required checks (`checks_failed` only). */
  failedChecks?: string[];
  /** What the failed checks reported for the current head (`checks_failed` only). */
  findings?: VerificationFinding[];
  mergeCommitSha?: string;
  /** Owner ref from the draft marker — attribution, not permission. */
  importer?: string;
  observedAt: string;
};

export interface CloudflareWorkersApi {
  getWorkers(): Promise<WorkersListResponse>;
  /**
   * Account-wide visits, last publish and idle marks for every app.
   * Platform-team only: rejects with an `ApiError` of status 403 otherwise.
   */
  getAppActivity(): Promise<AppActivityResponse>;
  getWorkerDetail(name: string): Promise<WorkerDetailResponse>;
  getWorkerMetrics(
    name: string,
    range: WorkerMetricsRange,
  ): Promise<WorkerMetricsResponse>;
  getWorkerLogs(
    name: string,
    range: WorkerMetricsRange,
    options?: { errorsOnly?: boolean; cursor?: string },
  ): Promise<WorkerLogsResponse>;
  getWorkerVersionConfig(
    name: string,
    versionId: string,
  ): Promise<VersionConfig>;
  getWorkerRepoConfig(name: string): Promise<WorkerRepoConfig>;
  refresh(): Promise<RefreshResponse>;
  putVariable(
    workerName: string,
    varName: string,
    value: string,
  ): Promise<void>;
  deleteVariable(workerName: string, varName: string): Promise<void>;
  putSecret(
    workerName: string,
    secretName: string,
    value: string,
  ): Promise<void>;
  deleteSecret(workerName: string, secretName: string): Promise<void>;
  /**
   * Uploads a project zip ahead of a scaffolder task, returning an id to pass
   * as the task's `projectUpload` value instead of an inline base64 payload.
   */
  uploadProject(zip: Blob): Promise<UploadProjectResponse>;
  /**
   * Checks a pending upload against the supported app shapes (Cloud Agents
   * flow, DevProd only) without claiming it. Resolves for both accepted and
   * rejected uploads; throws only when the check itself fails.
   */
  assessUpload(uploadId: string): Promise<UploadAssessment>;
  getPendingUpdates(): Promise<PendingUpdate[]>;
  abandonPendingUpdate(pullRequestNumber: number, app: string): Promise<void>;
  /**
   * Where in the publish pipeline the app's most recent change is. Cheap to
   * poll: the backend memoizes per app for a few seconds.
   */
  getPublishStatus(name: string): Promise<PublishStatus>;
  /** The code of the app's live version as a zip rooted at `<name>/`. */
  downloadSource(name: string): Promise<SourceDownload>;
  /** Curated discovery digest read from `apps/spotlight/content/snapshot.json`. */
  getSpotlight?(): Promise<SpotlightDigestResponse>;
}

export interface SourceDownload {
  blob: Blob;
  fileName: string;
}

export type SpotlightCurationStatus = 'featured' | 'listed' | 'excluded';

export type SpotlightDigestApp = {
  directory: string;
  workerName: string;
  address: string | null;
  owner: string | null;
  status: SpotlightCurationStatus;
  title: string | null;
  tagline: string | null;
  purpose: string | null;
  audience: string[];
  category: string | null;
  capabilities: string[];
  firstShippedOn: string | null;
  lastShippedOn: string | null;
};

export type SpotlightDigestResponse = {
  available: boolean;
  generatedAt: string | null;
  sourceCommit: string | null;
  apps: SpotlightDigestApp[];
  cached: boolean;
  cachedAt: string | null;
};

export const cloudflareWorkersApiRef =
  createApiRef<CloudflareWorkersApi>().with({
    id: 'plugin.cloudflare-workers.api',
  });
